import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import CardStack from "@/components/swipe/card_stack";
import RenterCard from "@/components/renter_browse/renter_card";
import RenterExpand from "@/components/renter_browse/renter_expand";
import RenterActions from "@/components/renter_browse/renter_actions";
import MatchModal from "@/components/renter_browse/match_modal";
import CompleteProfileGate from "@/components/profile/complete_profile_gate";
import VerifyGateModal from "@/components/gates/verify_gate_modal";
import SwipeLimitModal from "@/components/gates/swipe_limit_modal";
import ButtonGhost from "@/components/buttons/button_ghost";
import FALLBACK_RENTERS from "@/components/renter_browse/fallback_renters";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { mockRenters } from "@/data/listings";

// FIX 3 — the primary swipe deck in LISTER mode shows RENTER cards (the reverse
// deck), so a lister swipes on people looking for a place instead of listings.
// This is the same renter-deck experience as /lister/browse, surfaced through
// the main /swipe entry. The backend GET /api/swipes/deck?type=renters already
// ranks renters (and gates on lister mode); when it has no data (or the account
// is a demo session) we seed the local demo renters so the deck is never empty.
function seedDeck() {
  const renters =
    (Array.isArray(mockRenters) && mockRenters.length ? mockRenters : null) || FALLBACK_RENTERS;
  return renters.map((r, i) => ({ id: r.id ?? `renter_${i}`, ...r }));
}

// `fullBleed`: the /swipe and /lister/browse pages render the deck Tinder
// style on mobile (edge to edge card between the top nav and the bottom bar,
// actions floating over the card bottom) — the exact layout the renter-side
// SwipeDeck uses. The landing page hero keeps the compact centered card, so
// full bleed is opt in rather than the default.
export default function RenterSwipeDeck({ fullBleed = false }) {
  const { user } = useAuth();
  const { refreshNow } = useNotifications();
  const [deck, setDeck] = useState([]);
  const [index, setIndex] = useState(0);
  const [match, setMatch] = useState(null);
  const [profileGate, setProfileGate] = useState(null); // null | { missing: [] }
  // Identity gates: 403 VERIFICATION_REQUIRED (unverified account) and
  // 403 SWIPE_LIMIT (free daily cap) from POST /swipes.
  const [verifyGate, setVerifyGate] = useState(false);
  const [limitGate, setLimitGate] = useState(false);
  const [viewerNoticeDismissed, setViewerNoticeDismissed] = useState(false);
  const [expand, setExpand] = useState(null);
  const [undone, setUndone] = useState(null);
  const stackRef = useRef(null);
  const navigate = useNavigate();
  // Likes on renters are always FOR one of the lister's ACTIVE listings.
  // null = still loading; [] = none (deck replaced by the "publish first"
  // gate, mirroring the backend's 403 LISTING_REQUIRED). With several
  // listings the selector above the deck says which home a like is for.
  const [myListings, setMyListings] = useState(null);
  const [forListing, setForListing] = useState("");

  useEffect(() => {
    if (!user || user.demo) { setMyListings([]); return undefined; }
    let alive = true;
    api
      .get("/listings/mine")
      .then((res) => {
        if (!alive) return;
        const list = (res.data?.listings || res.data || []).filter(
          (l) => l.status === "active" && !l.deleted);
        setMyListings(list);
        if (list.length) setForListing(list[0].id || list[0]._id);
      })
      .catch(() => alive && setMyListings([]));
    return () => { alive = false; };
  }, [user]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/swipes/deck", { params: { type: "renters" } });
        const list = Array.isArray(data) ? data : data?.deck;
        if (alive && Array.isArray(list) && list.length) return setDeck(list);
      } catch {}
      // Seeded demo renters exist for demo sessions and offline dev so the
      // primary lister swipe deck is populated; a real account with an empty
      // deck gets the honest "that is everyone" state.
      if (alive) setDeck(user?.demo ? seedDeck() : []);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.demo]);

  const current = deck[index];
  const remaining = useMemo(() => deck.slice(index), [deck, index]);

  const swipeTop = (action) => stackRef.current?.swipeTop(action) ?? false;

  function swipe(action, renter = current) {
    if (!renter) return false;
    if (user && !user.demo && user.profile_complete === false) {
      setProfileGate({ missing: user.missing_fields || [] });
      return false;
    }
    setUndone(null);
    setIndex((i) => i + 1);
    finishSwipe(action, renter);
    return true;
  }

  async function finishSwipe(action, renter) {
    try {
      const { data } = await api.post("/swipes", {
        target_type: "renter",
        target_id: renter.id,
        action,
        // Which home this like is for (the selector above the deck).
        listing_id: forListing || undefined,
      });
      // A swipe can create a match (badge!) the instant it lands, so pull
      // fresh notification counters instead of waiting for the next poll.
      refreshNow();
      if (data?.match) return setMatch(renter);
    } catch (error) {
      // No active listing: restore the card; the gate panel explains.
      if (error?.response?.status === 403 && error.response.data?.code === "LISTING_REQUIRED") {
        setIndex((i) => Math.max(0, i - 1));
        setUndone({ id: renter.id, dir: action === "pass" ? -1 : 1 });
        setMyListings([]);
        return;
      }
      // Several listings and the like did not say which one: restore and
      // ask via toast (the selector should normally prevent this).
      if (error?.response?.status === 409 && error.response.data?.code === "LISTING_CHOICE_REQUIRED") {
        setIndex((i) => Math.max(0, i - 1));
        setUndone({ id: renter.id, dir: action === "pass" ? -1 : 1 });
        toast.info("Choose which listing you are liking renters for, just above the deck.");
        return;
      }
      if (error?.response?.status === 403 && error.response.data?.code === "PROFILE_INCOMPLETE") {
        setIndex((i) => Math.max(0, i - 1));
        setUndone({ id: renter.id, dir: action === "pass" ? -1 : 1 });
        setProfileGate({ missing: error.response.data?.missing || [] });
        return;
      }
      // Identity-unverified accounts browse only: restore the card and open
      // the verification gate instead of losing the swipe silently.
      if (error?.response?.status === 403 && error.response.data?.code === "VERIFICATION_REQUIRED") {
        setIndex((i) => Math.max(0, i - 1));
        setUndone({ id: renter.id, dir: action === "pass" ? -1 : 1 });
        setVerifyGate(true);
        return;
      }
      // Free daily cap reached: restore the card and open the paywall.
      if (error?.response?.status === 403 && error.response.data?.code === "SWIPE_LIMIT") {
        setIndex((i) => Math.max(0, i - 1));
        setUndone({ id: renter.id, dir: action === "pass" ? -1 : 1 });
        setLimitGate(true);
        return;
      }
      // Pretend matches are DEMO ONLY: a real lister must never celebrate a
      // match the server did not create, even when the request went offline.
      if (user?.demo && !error?.response && action !== "pass" && (renter.likes_you || Math.random() < 0.34)) {
        return setMatch(renter);
      }
    }
    if (action === "super")
      toast("Super like sent", { description: `${renter.name || renter.first_name} will see you first.` });
  }

  // Viewer mode: signed in but identity unverified (and not a demo session).
  const showViewerNotice =
    !!user && !user.demo && !user.verified && user.kyc_status !== "approved" && !viewerNoticeDismissed;

  // No active listing: browsing renters is pointless (and the backend
  // refuses the swipes), so the deck is replaced by an honest gate.
  const showListingGate = !!user && !user.demo && Array.isArray(myListings) && myListings.length === 0;

  if (showListingGate) {
    return (
      <div className={fullBleed ? "flex h-full w-full flex-1 flex-col items-center justify-center px-4 lg:h-auto lg:flex-none" : "flex w-full flex-col items-center"}>
        <div className="relative mx-auto flex w-full max-w-[430px] flex-col items-center justify-center rounded-3xl bg-night-surface p-10 text-center shadow-sm ring-1 ring-night-line" style={{ minHeight: 420 }}>
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-night-tint">
            <Home className="h-7 w-7 text-brand-primary" />
          </div>
          <h2 className="mt-4 text-lg font-extrabold text-night-text">Publish a listing first</h2>
          <p className="mt-1 text-sm text-night-sub">
            Likes on renters are always for one of your homes. Publish your first listing
            (it is free) and come back to start matching.
          </p>
          <ButtonGhost className="mt-5" onClick={() => navigate("/lister/listings/new")}>
            Create your listing
          </ButtonGhost>
        </div>
      </div>
    );
  }

  // Layout skeleton mirrors the renter-side SwipeDeck exactly: in full-bleed
  // mode the card fills every pixel between the top nav and the bottom bar on
  // mobile (selector pill and notices FLOAT over the photo like deck notices,
  // action row floats near the card's bottom edge), while desktop keeps the
  // large centered card. The compact (landing hero) mode keeps the old flow.
  const rootClass = fullBleed
    ? "relative flex h-full w-full flex-1 flex-col items-center lg:h-auto lg:flex-none"
    : "flex w-full flex-col items-center";
  const overlayClass = fullBleed
    ? "absolute inset-x-4 top-3 z-30 space-y-2 lg:static lg:inset-auto lg:mb-3 lg:w-full lg:max-w-[430px] lg:space-y-3"
    : "mb-3 w-full max-w-[430px] space-y-3";
  const stackClass = fullBleed
    ? "relative min-h-0 w-full flex-1 lg:mx-auto lg:min-h-[560px] lg:w-full lg:max-w-[430px]"
    : "relative mx-auto w-full max-w-[430px] flex-1";
  const cardRound = fullBleed ? "rounded-none lg:rounded-3xl" : "rounded-3xl";

  return (
    <div className={rootClass}>
      {((Array.isArray(myListings) && myListings.length > 1) || showViewerNotice) && (
        <div className={overlayClass}>
          {/* Which listing are these likes for? Only shown with 2+ active
              listings; a single listing is attributed automatically. In full
              bleed it floats over the top of the card like the deck notices,
              so it gets the frosted treatment for legibility on photos. */}
          {Array.isArray(myListings) && myListings.length > 1 && (
            <div className={`flex w-full items-center gap-2 rounded-xl border px-3 py-2 ${fullBleed ? "border-night-line bg-night-surface/90 shadow-ambient backdrop-blur lg:border-night-line lg:bg-night-surface lg:shadow-none" : "border-night-line bg-night-surface"}`}>
              <Home size={14} className="shrink-0 text-brand-primary" />
              <label htmlFor="like-for-listing" className="shrink-0 text-xs font-semibold text-night-sub">
                Liking renters for
              </label>
              <select
                id="like-for-listing"
                value={forListing}
                onChange={(e) => setForListing(e.target.value)}
                className="min-w-0 flex-1 truncate rounded-lg bg-transparent text-sm font-semibold text-night-text outline-none"
              >
                {myListings.map((l) => (
                  <option key={l.id || l._id} value={l.id || l._id}>
                    {l.title || "Untitled listing"}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Slim viewer mode notice, styled like the deck's broaden notice:
              emerald hairline, dismissible, shares the floating slot. */}
          {showViewerNotice && (
            <div className={`relative w-full rounded-xl border-l-2 border-brand-primary bg-night-tint px-3.5 py-2 pr-9 ${fullBleed ? "shadow-ambient lg:shadow-none" : ""}`}>
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() => setViewerNoticeDismissed(true)}
                className="absolute right-1.5 top-1.5 rounded-full p-1 text-night-sub transition hover:text-night-text"
              >
                <X size={14} />
              </button>
              <p className="text-xs font-semibold text-night-text">You are browsing as a viewer.</p>
              <p className="mt-0.5 text-xs text-night-sub">
                Verify your identity to swipe and match.{" "}
                <Link to="/profile" className="font-semibold text-brand-primary underline underline-offset-2">
                  Verify
                </Link>
              </p>
            </div>
          )}
        </div>
      )}
      <div className={stackClass} style={fullBleed ? undefined : { minHeight: 560 }}>
        {remaining.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`absolute inset-0 flex flex-col items-center justify-center bg-night-surface p-8 text-center shadow-sm ring-1 ring-night-line ${cardRound}`}
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-night-tint">
              <RotateCcw className="h-7 w-7 text-brand-primary" />
            </div>
            <h2 className="mt-4 text-lg font-extrabold text-night-text">That is everyone for now</h2>
            <p className="mt-1 text-sm text-night-sub">
              New renters join every day. Check back soon or review your applicants.
            </p>
            <ButtonGhost className="mt-5" onClick={() => setIndex(0)}>
              Start over
            </ButtonGhost>
          </motion.div>
        ) : (
          <CardStack
            ref={stackRef}
            deck={remaining}
            onSwipe={(action, renter) => swipe(action, renter)}
            undone={undone}
            pose={(depth) => ({ y: depth * 12 })}
            veilClass={cardRound}
            renderCard={(renter, props) => (
              <RenterCard
                renter={renter}
                edge={fullBleed}
                onExpand={props.active ? () => setExpand({ renter }) : undefined}
                {...props}
              />
            )}
          />
        )}

        {/* Full bleed mobile: the action row floats ON the card near its
            bottom edge (classic Tinder). pointer-events sit on the buttons
            only so the card underneath stays draggable / tappable. */}
        {fullBleed && remaining.length > 0 && (
          <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 lg:hidden">
            <RenterActions
              disabled={!current}
              onPass={() => swipeTop("pass")}
              onLike={() => swipeTop("like")}
              onSuperLike={() => swipeTop("super")}
            />
          </div>
        )}
      </div>

      <div className={`mt-5 w-full max-w-[430px] ${fullBleed ? "hidden lg:block" : ""}`}>
        <RenterActions
          disabled={!current}
          onPass={() => swipeTop("pass")}
          onLike={() => swipeTop("like")}
          onSuperLike={() => swipeTop("super")}
        />
      </div>

      <RenterExpand
        open={!!expand}
        renter={expand?.renter}
        onClose={() => setExpand(null)}
        onAction={swipeTop}
      />

      <MatchModal open={!!match} renter={match} onClose={() => setMatch(null)} />
      <CompleteProfileGate
        open={!!profileGate}
        onClose={() => setProfileGate(null)}
        missing={profileGate?.missing || []}
        role={user?.role || "lister"}
      />
      <VerifyGateModal open={verifyGate} onClose={() => setVerifyGate(false)} />
      <SwipeLimitModal open={limitGate} onClose={() => setLimitGate(false)} />
    </div>
  );
}
